import { ReturnCode } from '../../constants/enums/returnCode.enum';
import { FetchOTPReq, OTPInfo, VerifyOTPReq } from 'bff/models/login';
import commonService from 'utils/commonService';
import apiService from './apiService';
import { BaseRes } from 'bff/models/base/baseRes';
import { MemberProfile } from 'bff/models/member';
import { SysOptionReq, NotifyEmail, Codes, SysOption, EmailDomains } from 'bff/models/info';
import { SysOptions } from 'bff/models/info/sysoptions';
import { SactionType } from 'constants/enums/sactionType.enum';
// import moment from 'moment';
import moment from 'moment-timezone';
import { BoPayby } from 'bff/models/member/boPayby';
import { DeliverBy, DeliverByType } from 'constants/enums/policyDelivery.enum';
import apiCommonService from 'bff/services/commonService';
import { SysOptType } from 'constants/enums/sysOptType.enum';
import { InsType } from 'constants/enums/insType.enum';
import { InfoCheckmailandmobileReq, InfoCheckmailandmobileResp } from 'bff/models/info/infoCheckmailandmobile';
import axiosService from 'utils/axiosService';

/**
 * @description 取得系統時間 (毫秒)
 */
const fetchSysTime = async (applicationId?: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig(applicationId);

  const res = await apiService.fetchSysTime(baseReq, reqConfig).then((res: BaseRes<any>) => res);

  if (res && res.MWHEADER && res.MWHEADER.RETURNCODE === ReturnCode.CodeSuccess &&
    res.TRANRS && res.TRANRS.SERVER_TIME) {
    return moment(res.TRANRS.SERVER_TIME).isValid()
      ? res.TRANRS.SERVER_TIME
      // : new Date().getTime();
      : moment().valueOf();
  }

  // return new Date().getTime();
  return moment().valueOf();
};

/**
 * @description 取得 OTP 驗證碼方法
 * @param memberProfile 使用者資料
 * @param sactionType OTP 使用情境 1-會員登入，2-會員註冊，3-投保
 * @param isReSent 是否為重發
 * @param applicationId Application Id
 */
const fetchOTP = async (memberProfile: MemberProfile, sactionType: string, isReSent?: boolean, applicationId?: string, agreeTimeTerms?: string) => {
  // 發送 OTP 認證碼的 Request Model
  const fetchOTPReq: FetchOTPReq = {
    MEMBER_ID: memberProfile.MEMBER_ID,
    MOBILE: memberProfile.MOBILE,
    EMAIL: memberProfile.EMAIL_ADDR,
    // 固定帶 0
    PREFIX_LENGTH: 0,
    // 固定帶 6
    OTP_LENGTH: 6,
    // 重發註記 0-第一次發送, 1-重發
    RESEND: isReSent ? '1' : '0',
    // 訊息樣板的ID 固定帶 OP01
    TEMPLATE_ID: 'OP01',
    // OTP 使用情境 1-會員登入，2-會員註冊，3-投保
    SACTION: sactionType,
    // F-前台或B-後台
    SOURCE: 'F',
    AGREE_TIME_TERMS: agreeTimeTerms
  };
  const baseReq = commonService.getApiReqData(fetchOTPReq);
  const reqConfig = commonService.getReqConfig(applicationId);
  // console.log('authenticate', baseReq);
  const res = await apiService.fetchOTP(baseReq, reqConfig).then((res: BaseRes<OTPInfo>) => res);
  // console.log('authenticate', result);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

/**
 * @description 驗證 OTP 驗證碼方法
 * @param authenticationRes 發送 OTP 認證碼回傳的結果資訊
 * @param otpCode OTP 驗證碼
 * @param sactionType OTP 使用情境 1-會員登入，2-會員註冊，3-投保
 * @param createToken 是否產生 Token
 */
const verifyOTP = async (authenticationRes: OTPInfo, otpCode: string, sactionType: string, createToken?: boolean, applicationId?: string, agreeTimeTerms?: string) => {
  const verifyOTPReq: VerifyOTPReq = {
    SN: `${authenticationRes.SN}`,
    PREFIX_CODE: authenticationRes.PREFIX_CODE,
    OTP_CODE: otpCode,
    // OTP 使用情境 1-會員登入，2-會員註冊，3-投保
    SACTION: sactionType,
    // OTP 驗證成功後是否產生 token 回覆 未帶或為 0–不產生 token 1-產生 token
    RTK: createToken ? '1' : '0',
    AGREE_TIME_TERMS: agreeTimeTerms
  };
  // console.log('verifyOTPReq', verifyOTPReq);
  const baseReq = commonService.getApiReqData(verifyOTPReq);
  const reqConfig = commonService.getReqConfig(applicationId);

  // console.log('verifyOTPReq', JSON.stringify(verifyOTPReq));

  const result = await apiService.verifyOTP(baseReq, reqConfig).then((res: any) => res);
  // console.log('verify OTP result', result);

  if (result.MWHEADER && result.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(result.MWHEADER.RETURNDESC, result.MWHEADER.RETURNCODE);

    if (sactionType === SactionType.Signup) return result.MWHEADER.RETURNDESC;
  } else {
    return { ...result };
  }
};

/**
 * @description 會員登出
*/
const logOut = async () => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.logOut(baseReq, reqConfig).then((res: BaseRes<any>) => res);
  // console.log('logout res', res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.jti;
  }
};

/**
 * @description 取得系統參數資料
 */
export const fetchSysOpts = async (sysOptionReq: SysOptionReq) => {
  const sysOptionBaseReq = commonService.getApiReqData(sysOptionReq);
  const reqConfig = commonService.getReqConfig();
  const res = await apiService.fetchSysOptions(sysOptionBaseReq, reqConfig).then((res: BaseRes<SysOptions>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return { ...res.TRANRS };
  }
};

/**
 * @description 發送電子郵件
 * @param notifyEmailReq 電子郵件資料
 * @param applicationId Application Id
 */
const notifyEmail = async (notifyEmailReq: NotifyEmail, applicationId?: string) => {
  const baseReq = commonService.getApiReqData(notifyEmailReq);
  const reqConfig = commonService.getReqConfig(applicationId);

  const res = await apiService.notifyEmail(baseReq, reqConfig).then((res: BaseRes<any>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE, '完成通知信-發送失敗');
  } else {
    return { ...res.TRANRS };
  }
};

/**
 * @description 檢核專案代碼
 * @param programId 專案代碼
*/
const verifyProgramId = async (programId: string, insTypeId: string) => {
  const baseReq = commonService.getApiReqData({ INS_TYPE_ID: insTypeId });
  const reqConfig = commonService.getReqConfig();

  const result = await apiService.verifyProgramId(baseReq, reqConfig, programId).then((res: BaseRes<any>) => res);

  if (result.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    return result.TRANRS.ERROR_MESSAGE;
  } else {
    return result.TRANRS;
  }
};

/**
 * @description 取得 Code Table 資訊
*/
const fetchCodeTables = async (codeClass: string, codeId?: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchCodes(baseReq, reqConfig, codeClass, codeId).then((res: BaseRes<Codes>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return commonService.arrayTypeReturn(res.TRANRS.CODES);
  }
};

/**
 * @description 取得 每個險種的保單寄送方式
*/
const getDeliveryMethods = async (insType: InsType) => {
  console.log('🚀 getDeliveryMethods ~ insType:', insType);
  // const deliveryMethods = async (deliveryBy: DeliverByType | undefined) => {
  const res = await apiCommonService.fetchSysOpts({ PCLASS: [SysOptType.PODELIVER_BY, SysOptType.PODELIVERY] });
  if (res) {
    const methods = commonService.sortSysOpts(commonService.arrayTypeReturn(res[SysOptType.PODELIVERY]));
    const deliverBy = res[SysOptType.PODELIVER_BY].find((item: SysOption) => item.PARAM_ID === insType)?.PARAM_VALUE as DeliverByType;
    switch (deliverBy) {
      case DeliverBy.Electric:
        return [methods[0]];
      case DeliverBy.Paper:
        return [methods[1]];
      case DeliverBy.ElectricAndPaper:
        return [methods[0], methods[1]];
      default:
        return [methods[0]];
    }
  }
};

/**
 * @description 驗證繳費 UUID
 * @param uuid 繳費辨識用 UUID
*/
const boPayby = async (uuid: string) => {
  const baseReq = commonService.getApiReqData({ PAYMENT_UUID: uuid });
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.boPayby(baseReq, reqConfig).then((res: BaseRes<BoPayby>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    return `${res.MWHEADER.RETURNDESC}<br />(error code : ${res.MWHEADER.RETURNCODE})`;
  } else {
    return res.TRANRS;
  }
};

/**
 * @description 手機與電子郵件檢核
 * @param args 手機與電子郵件資料
 * @param applicationId Application Id
 */
const validMailAndMobile = async (args: InfoCheckmailandmobileReq, applicationId?: string) => {
  const argsModel = commonService.getApiReqData(args);
  const config = commonService.getReqConfig(applicationId);
  const response: BaseRes<InfoCheckmailandmobileResp> = await axiosService.post('/info/checkmailandmobile', argsModel, config);
  if (response.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(response.MWHEADER.RETURNDESC, response.MWHEADER.RETURNCODE);
  } else {
    return response.TRANRS;
  }
};

/**
 * @description 取得黑名單網域
 */
const fetchBlockDomains = async () => {
  const baseReq = commonService.getApiReqData({ QUERY_TYPE: 'B' });
  const reqConfig = commonService.getReqConfig();
  const res: BaseRes<EmailDomains> = await apiService.fetchEmailDN(baseReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.BLOCKDN;
  }
};

/**
 * @description 查詢是否為網投會員（續保驗證用）
 * @param memberId 身分證字號/居留證號
 */
const queryMemberForRenewal = async (memberId: string) => {
  const baseReq = commonService.getApiReqData({ MEMBER_ID: memberId });
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchMemberProfile(baseReq, reqConfig).then((res: BaseRes<any>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    return null;
  }

  return res.TRANRS;
};

export default {
  fetchSysTime,
  fetchOTP,
  verifyOTP,
  logOut,
  fetchSysOpts,
  notifyEmail,
  verifyProgramId,
  fetchCodeTables,
  boPayby,
  getDeliveryMethods,
  validMailAndMobile,
  fetchBlockDomains,
  queryMemberForRenewal
};
